import { z } from 'zod';
import { allocate } from './sales-allocate.js';
import { isIsoDate } from './sales-dates.js';
import { SALES_PAYMENT_STATES, SALES_TREATMENTS, } from './sales-types.js';
/** What an order counts at under a given treatment, before splitting into parts. */
export function amountFor(treatment, order, money, customAmountMinor = null) {
    switch (treatment) {
        case 'count_payments':
            return Math.max(0, Math.min(money.netPaidMinor, order.totalMinor));
        case 'count_order_total':
            return Math.max(0, order.totalMinor - money.refundedMinor);
        case 'count_custom':
            return Math.max(0, customAmountMinor ?? 0);
        case 'exclude':
            return 0;
    }
}
/** The basis treatment for an order nobody has raised a question about. */
export function basisTreatment(order, money) {
    if (order.state === 'cancelled')
        return 'exclude';
    if (order.basis === 'payments')
        return money.netPaidMinor > 0 ? 'count_payments' : 'exclude';
    return order.state === 'pending' ? 'exclude' : 'count_order_total';
}
const TIE_ORDER = ['exclude', 'count_payments', 'count_order_total', 'count_custom'];
export function recognize(order, money, options = {}) {
    let treatment;
    let decidedBy;
    let custom = null;
    if (options.override) {
        treatment = options.override.treatment;
        custom = options.override.customAmountMinor;
        decidedBy = 'override';
    }
    else if (options.candidateTreatments && options.candidateTreatments.length > 0) {
        const candidates = [...new Set(options.candidateTreatments)].filter((t) => t !== 'count_custom');
        candidates.sort((a, b) => amountFor(a, order, money) - amountFor(b, order, money) || TIE_ORDER.indexOf(a) - TIE_ORDER.indexOf(b));
        treatment = candidates[0] ?? basisTreatment(order, money);
        decidedBy = candidates.length > 0 ? 'review' : 'basis';
    }
    else {
        treatment = basisTreatment(order, money);
        decidedBy = 'basis';
    }
    const recognizedMinor = amountFor(treatment, order, money, custom);
    const taxPart = order.taxRemittedByChannel ? 0 : order.taxMinor;
    const weights = [order.itemsGrossMinor - order.discountMinor, order.shippingMinor, taxPart];
    const hasWeight = weights.some((w) => w > 0);
    const [netSalesMinor = 0, shippingMinor = 0, taxMinor = 0] = allocate(recognizedMinor, hasWeight ? weights : [1, 0, 0]);
    return {
        treatment,
        decidedBy,
        countable: recognizedMinor > 0,
        recognizedMinor,
        netSalesMinor,
        shippingMinor,
        taxMinor,
    };
}
/**
 * Spreads an order's recognized amounts, discounts, refunds and fees over its
 * lines so SKU figures add up to exactly the order's. Each column is allocated
 * by the line's merchandise value; tax goes by each line's own tax where the
 * channel reported one.
 */
export function splitAcrossLines(lines, order, recognition, adjustments, feesMinor) {
    if (lines.length === 0)
        return [];
    const merchandise = lines.map((l) => Math.max(0, l.grossMinor - l.lineDiscountMinor));
    const gross = lines.map((l) => l.grossMinor);
    const lineTax = lines.map((l) => l.lineTaxMinor);
    const netSales = allocate(recognition.netSalesMinor, merchandise);
    const shipping = allocate(recognition.shippingMinor, merchandise);
    const tax = allocate(recognition.taxMinor, lineTax.some((t) => t > 0) ? lineTax : merchandise);
    const lineDiscountTotal = lines.reduce((sum, l) => sum + l.lineDiscountMinor, 0);
    const orderLevelDiscount = allocate(Math.max(0, order.discountMinor - lineDiscountTotal), gross);
    const index = new Map(lines.map((l, i) => [l.externalLineId, i]));
    const direct = lines.map(() => 0);
    let unassigned = 0;
    for (const adjustment of adjustments) {
        const i = adjustment.itemExternalLineId ? index.get(adjustment.itemExternalLineId) : undefined;
        if (i === undefined)
            unassigned += adjustment.amountMinor;
        else
            direct[i] = (direct[i] ?? 0) + adjustment.amountMinor;
    }
    const spread = allocate(unassigned, merchandise);
    const fees = feesMinor === null ? null : allocate(feesMinor, merchandise);
    return lines.map((line, i) => ({
        externalLineId: line.externalLineId,
        netSalesMinor: netSales[i] ?? 0,
        discountMinor: line.lineDiscountMinor + (orderLevelDiscount[i] ?? 0),
        shippingMinor: shipping[i] ?? 0,
        taxMinor: tax[i] ?? 0,
        refundedMinor: (direct[i] ?? 0) + (spread[i] ?? 0),
        feesMinor: fees === null ? null : (fees[i] ?? 0),
    }));
}
// ---- resolution rules -------------------------------------------------------
const isoDate = z.string().refine(isIsoDate, 'Expected a date as YYYY-MM-DD');
/**
 * Which review issues a saved resolution rule decides. Every present field must
 * match; `ruleIds` is required so a rule can never reach beyond the questions
 * it was written for.
 */
export const salesResolutionMatchSchema = z.object({
    ruleIds: z.array(z.string().min(1)).min(1),
    channelIds: z.array(z.string().min(1)).optional(),
    /** Inclusive local order dates. */
    placedFrom: isoDate.optional(),
    placedTo: isoDate.optional(),
    channelStatuses: z.array(z.string()).optional(),
    paymentStates: z.array(z.enum(SALES_PAYMENT_STATES)).optional(),
});
export const salesTreatmentSchema = z.enum(SALES_TREATMENTS);
export function matchesResolution(match, subject) {
    if (!match.ruleIds.includes(subject.ruleId))
        return false;
    if (match.channelIds && !match.channelIds.includes(subject.channelId))
        return false;
    if (match.placedFrom && subject.placedDate < match.placedFrom)
        return false;
    if (match.placedTo && subject.placedDate > match.placedTo)
        return false;
    if (match.channelStatuses) {
        const wanted = match.channelStatuses.map((s) => s.trim().toLowerCase());
        if (!wanted.includes(subject.channelStatus.trim().toLowerCase()))
            return false;
    }
    if (match.paymentStates && !match.paymentStates.includes(subject.paymentState))
        return false;
    return true;
}
//# sourceMappingURL=sales-recognition.js.map